// 通用 JSON 截断补全器
//
// 处理 LLM 流式输出在任意字符位置被 provider 安全策略 / 网络中断截断的场景。
// 单次字符扫描 + 栈，追踪嵌套结构、字符串转义、数字状态，扫完后按末态合成闭合尾。
// 同时记录「截断点路径」与「是否落在 text 字段字符串内部」两个领域信号，由调用方做业务裁剪。

export type PathSegment = string | number
export type Path = PathSegment[]

export interface CloseResult {
  repaired: string
  // 截断位置落在某个字符串值内部，且该字段是「被视为正文」的字段（默认 text / content）时，
  // 记录「包含该字段的对象」在根上的路径；调用方据此丢弃这条未写完的消息。
  // 若截断发生在根对象（无父容器），dropPath 为空数组 []，含义是「整个根对象不可用」。
  dropPath: Path | null
  // 扫描过程中是否发生了任何补齐（尾部字符串/数字/括号等）。完整 JSON 时为 false，表示未改动结构。
  repairedAny: boolean
}

const TEXT_FIELD_KEYS = new Set(['text', 'content'])

interface Frame {
  kind: 'object' | 'array'
  // 该 frame 在父结构中的路径，不含自身 index/key
  path: Path
  // 开启该 frame 的 `{` / `[` 字节位置，rollback 回到更早位置时用于判定是否要弹该 frame
  startIdx: number
  // object 下一次期望 key 还是 value；array 下一次期望 item
  expecting: 'keyOrClose' | 'colon' | 'valueOrClose' | 'commaOrClose'
  // object 下最近一次看到的 key，仅在 'colon' / 'valueOrClose' 时有意义
  currentKey?: string
  // array 下当前 index
  nextIndex: number
}

export function closeTruncatedJson(input: string): CloseResult | null {
  if (typeof input !== 'string' || !input.trim())
    return null

  const startIdx = findJsonStart(input)
  if (startIdx < 0)
    return null

  const src = input.slice(startIdx)

  const stack: Frame[] = []
  let i = 0
  const n = src.length

  // 最近一次「合法结构边界」之后的位置（exclusive end）。
  // 用于在末尾出现孤立 `,` / 孤儿 `"key":` 时把输入回切到此处。
  let lastSafeEnd = 0

  // 当前是否处于字符串内；字符串以 key 还是 value 身份出现
  let inString = false
  let stringRole: 'key' | 'value' | null = null
  let escape = false
  let unicodeRemaining = 0
  let stringStartIdx = -1

  // 当前是否处于数字
  let inNumber = false
  let numberStartIdx = -1

  // 若字符串在截断时未闭合且是 text/content 字段的 value，记录其所在栈帧信息
  let truncatedStringFrameDepth = -1

  // 根对象/数组完成后遇到垃圾尾 → 置 true 终止扫描，不再 return null
  let rootClosedAt = -1
  let stopScan = false

  function currentFrame(): Frame | undefined {
    return stack[stack.length - 1]
  }

  function afterValueTransition() {
    const f = currentFrame()
    if (!f)
      return
    if (f.kind === 'object') {
      f.currentKey = undefined
      f.expecting = 'commaOrClose'
    }
    else {
      f.nextIndex += 1
      f.expecting = 'commaOrClose'
    }
  }

  function pushFrame(kind: 'object' | 'array', idx: number) {
    const parent = currentFrame()
    let path: Path = []
    if (parent) {
      if (parent.kind === 'object')
        path = [...parent.path, parent.currentKey ?? '']
      else
        path = [...parent.path, parent.nextIndex]
    }
    stack.push({
      kind,
      path,
      startIdx: idx,
      expecting: kind === 'object' ? 'keyOrClose' : 'valueOrClose',
      nextIndex: 0,
    })
  }

  while (i < n && !stopScan) {
    const ch = src[i]

    if (inString) {
      if (escape) {
        escape = false
        if (ch === 'u')
          unicodeRemaining = 4
        i += 1
        continue
      }
      if (unicodeRemaining > 0) {
        if (isHexDigit(ch)) {
          unicodeRemaining -= 1
          i += 1
          continue
        }
        unicodeRemaining = 0
        i += 1
        continue
      }
      if (ch === '\\') {
        escape = true
        i += 1
        continue
      }
      if (ch === '"') {
        inString = false
        i += 1
        const f = currentFrame()
        if (!f) {
          // 理论不可达：inString=true 要求 currentFrame 存在（进入字符串时有 frame 检查）
          return null
        }
        if (stringRole === 'key') {
          f.currentKey = src.slice(stringStartIdx + 1, i - 1)
          f.expecting = 'colon'
        }
        else {
          afterValueTransition()
          lastSafeEnd = i
        }
        stringRole = null
        stringStartIdx = -1
        continue
      }
      i += 1
      continue
    }

    if (inNumber) {
      if (isNumberBodyChar(ch)) {
        i += 1
        continue
      }
      inNumber = false
      const numLiteral = src.slice(numberStartIdx, i)
      if (isCompleteNumber(numLiteral)) {
        afterValueTransition()
        lastSafeEnd = i
      }
      else {
        // 数字语法非法（如 `-` 后紧跟非数字）：保守返回 null
        return null
      }
      numberStartIdx = -1
      // 继续处理当前字符
    }

    if (isWhitespace(ch)) {
      i += 1
      continue
    }

    const f = currentFrame()

    // 根对象/数组已闭合，且遇到非空白字符 → 认为是 provider 追加的解释文字。
    // 置 stopScan 终止循环，用 rootClosedAt 作为 repaired 的 end（丢弃垃圾尾）。
    if (!f && rootClosedAt >= 0) {
      stopScan = true
      continue
    }

    if (ch === '{') {
      pushFrame('object', i)
      i += 1
      lastSafeEnd = i
      continue
    }
    if (ch === '[') {
      pushFrame('array', i)
      i += 1
      lastSafeEnd = i
      continue
    }
    if (ch === '}') {
      if (!f || f.kind !== 'object')
        return null
      stack.pop()
      i += 1
      if (stack.length === 0) {
        lastSafeEnd = i
        rootClosedAt = i
      }
      else {
        afterValueTransition()
        lastSafeEnd = i
      }
      continue
    }
    if (ch === ']') {
      if (!f || f.kind !== 'array')
        return null
      stack.pop()
      i += 1
      if (stack.length === 0) {
        lastSafeEnd = i
        rootClosedAt = i
      }
      else {
        afterValueTransition()
        lastSafeEnd = i
      }
      continue
    }
    if (ch === ',') {
      if (!f)
        return null
      f.expecting = f.kind === 'object' ? 'keyOrClose' : 'valueOrClose'
      // 逗号不是安全边界：若之后立即截断，需要把逗号也裁掉。
      i += 1
      continue
    }
    if (ch === ':') {
      if (!f || f.kind !== 'object' || f.expecting !== 'colon')
        return null
      f.expecting = 'valueOrClose'
      i += 1
      continue
    }
    if (ch === '"') {
      if (!f)
        return null
      stringStartIdx = i
      if (f.kind === 'object' && f.expecting === 'keyOrClose') {
        stringRole = 'key'
      }
      else if (f.kind === 'object' && f.expecting === 'valueOrClose') {
        stringRole = 'value'
      }
      else if (f.kind === 'array' && f.expecting === 'valueOrClose') {
        stringRole = 'value'
      }
      else {
        return null
      }
      inString = true
      i += 1
      continue
    }
    if (isNumberStartChar(ch)) {
      if (!f)
        return null
      if ((f.kind === 'object' && f.expecting !== 'valueOrClose')
        || (f.kind === 'array' && f.expecting !== 'valueOrClose')) {
        return null
      }
      inNumber = true
      numberStartIdx = i
      i += 1
      continue
    }
    if (ch === 't' || ch === 'f' || ch === 'n') {
      const literal = ch === 't' ? 'true' : ch === 'f' ? 'false' : 'null'
      if (src.slice(i, i + literal.length) === literal) {
        if (!f)
          return null
        afterValueTransition()
        i += literal.length
        lastSafeEnd = i
        continue
      }
      // 截断在布尔/空值里：当作未完成 value，留给末态处理
      stopScan = true
      continue
    }
    return null
  }

  // ====== 扫描结束，处理末态 ======

  // 根对象已闭合但后面有垃圾尾：直接截到 rootClosedAt
  if (rootClosedAt >= 0 && stack.length === 0) {
    return { repaired: src.slice(0, rootClosedAt), dropPath: null, repairedAny: false }
  }

  let repaired = src
  let dropPath: Path | null = null
  let repairedAny = false

  if (inString) {
    const f = currentFrame()
    if (f && stringRole === 'value') {
      const key = f.kind === 'object' ? f.currentKey : undefined
      if (key && TEXT_FIELD_KEYS.has(key))
        truncatedStringFrameDepth = stack.length - 1
    }

    // 修剪末尾的不完整转义
    let endIdx = repaired.length
    if (unicodeRemaining > 0) {
      const received = 4 - unicodeRemaining
      endIdx -= (received + 2)
    }
    else if (escape) {
      endIdx -= 1
    }
    repaired = `${repaired.slice(0, endIdx)}"`
    repairedAny = true

    if (stringRole === 'value') {
      afterValueTransition()
    }
    else {
      // key 被截断 → 整个 `"incompleteKey` 连同可能的前导 `,` 一并裁掉
      repaired = rollbackToSafeEnd(repaired, lastSafeEnd)
      popFramesBeyond(stack, lastSafeEnd)
      const f2 = currentFrame()
      if (f2 && f2.kind === 'object')
        f2.expecting = 'keyOrClose'
      repaired = stripTrailingOrphanComma(repaired)
      repairedAny = true
    }
    stringRole = null
  }
  else if (inNumber) {
    const numLiteral = src.slice(numberStartIdx)
    if (isCompleteNumber(numLiteral)) {
      // 已是合法数字，后续追加闭合符即可
    }
    else {
      let trimmed = numLiteral
      while (trimmed.length > 0 && !isCompleteNumber(trimmed))
        trimmed = trimmed.slice(0, -1)
      if (trimmed.length === 0) {
        repaired = rollbackToSafeEnd(src, lastSafeEnd)
        popFramesBeyond(stack, lastSafeEnd)
        const f2 = currentFrame()
        if (f2 && f2.kind === 'object') {
          f2.currentKey = undefined
          f2.expecting = 'commaOrClose'
          repaired = stripTrailingOrphanComma(repaired)
        }
      }
      else {
        repaired = `${src.slice(0, numberStartIdx)}${trimmed}`
        afterValueTransition()
      }
      repairedAny = true
    }
  }

  // 末尾孤立逗号 / 孤儿 `"key":` / 孤儿 `"key"`
  const f3 = currentFrame()
  if (f3) {
    if (f3.kind === 'object' && f3.expecting === 'colon') {
      repaired = rollbackToSafeEnd(repaired, lastSafeEnd)
      popFramesBeyond(stack, lastSafeEnd)
      repaired = stripTrailingOrphanComma(repaired)
      const f4 = currentFrame()
      if (f4 && f4.kind === 'object') {
        f4.currentKey = undefined
        f4.expecting = 'commaOrClose'
      }
      repairedAny = true
    }
    else if (f3.kind === 'object' && f3.expecting === 'valueOrClose' && f3.currentKey !== undefined) {
      repaired = rollbackToSafeEnd(repaired, lastSafeEnd)
      popFramesBeyond(stack, lastSafeEnd)
      repaired = stripTrailingOrphanComma(repaired)
      const f4 = currentFrame()
      if (f4 && f4.kind === 'object') {
        f4.currentKey = undefined
        f4.expecting = 'commaOrClose'
      }
      repairedAny = true
    }
    else if (f3.kind === 'object' && f3.expecting === 'keyOrClose') {
      repaired = stripTrailingOrphanComma(repaired)
    }
    else if (f3.kind === 'array' && f3.expecting === 'valueOrClose' && f3.nextIndex > 0) {
      repaired = stripTrailingOrphanComma(repaired)
    }
  }

  // 在栈未弹之前拿 dropPath
  if (truncatedStringFrameDepth >= 0) {
    const frame = stack[truncatedStringFrameDepth]
    if (frame)
      dropPath = [...frame.path]
  }

  // 栈逆序弹出，追加闭合符
  for (let d = stack.length - 1; d >= 0; d--) {
    const fr = stack[d]
    repaired += fr.kind === 'object' ? '}' : ']'
    repairedAny = true
  }

  return { repaired, dropPath, repairedAny }
}

function findJsonStart(s: string): number {
  for (let i = 0; i < s.length; i++) {
    const c = s[i]
    if (c === '{' || c === '[')
      return i
  }
  return -1
}

function isWhitespace(c: string): boolean {
  return c === ' ' || c === '\t' || c === '\n' || c === '\r'
}

function isHexDigit(c: string): boolean {
  return (c >= '0' && c <= '9') || (c >= 'a' && c <= 'f') || (c >= 'A' && c <= 'F')
}

function isNumberStartChar(c: string): boolean {
  return c === '-' || (c >= '0' && c <= '9')
}

function isNumberBodyChar(c: string): boolean {
  return (c >= '0' && c <= '9') || c === '.' || c === 'e' || c === 'E' || c === '+' || c === '-'
}

function isCompleteNumber(s: string): boolean {
  return /^-?(?:0|[1-9]\d*)(?:\.\d+)?(?:e[+-]?\d+)?$/i.test(s)
}

function rollbackToSafeEnd(s: string, safeEnd: number): string {
  if (safeEnd <= 0)
    return s
  if (safeEnd >= s.length)
    return s
  return s.slice(0, safeEnd)
}

function stripTrailingOrphanComma(s: string): string {
  let end = s.length
  while (end > 0 && isWhitespace(s[end - 1]))
    end -= 1
  if (end > 0 && s[end - 1] === ',')
    return s.slice(0, end - 1)
  return s
}

// rollback 后同步弹出已不在字符串中的 frame。
// 约束：frame.startIdx >= safeEnd 的都要弹掉。
function popFramesBeyond(stack: Frame[], safeEnd: number): void {
  while (stack.length > 0) {
    const top = stack[stack.length - 1]
    if (top.startIdx >= safeEnd)
      stack.pop()
    else
      break
  }
}

// 根据 dropPath 从 parse 后的结果中剪掉那个 mid-text 对象。
// - dropPath 为 []：截断发生在根对象，调用方应视为「根整体不可用」→ result 为 null。
// - dropPath 指向数组元素：splice 掉整个元素。
// - dropPath 指向对象字段（最后一段是 key）：delete 该字段，等价于把那条未写完的消息对象抹掉。
export function applyDropPath<T>(root: T, dropPath: Path): { mutated: boolean, result: T | null } {
  if (dropPath.length === 0)
    return { mutated: true, result: null }

  let parent: unknown = root
  for (let i = 0; i < dropPath.length - 1; i++) {
    const seg = dropPath[i]
    if (parent == null || typeof parent !== 'object')
      return { mutated: false, result: root }
    parent = (parent as Record<string | number, unknown>)[seg as string | number]
  }

  if (parent == null || typeof parent !== 'object')
    return { mutated: false, result: root }

  const last = dropPath[dropPath.length - 1]
  if (Array.isArray(parent) && typeof last === 'number') {
    if (last >= 0 && last < parent.length) {
      parent.splice(last, 1)
      return { mutated: true, result: root }
    }
  }
  else if (!Array.isArray(parent) && typeof last === 'string') {
    const obj = parent as Record<string, unknown>
    if (last in obj) {
      delete obj[last]
      return { mutated: true, result: root }
    }
  }
  return { mutated: false, result: root }
}
